import { Tooltip, TooltipProps } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';

const TOOLTIP_CLOSE_DELAY_MS = 120;

interface ExpandableTextTooltipProps {
  value: string
  dataCy: string
  contentRef: React.RefObject<HTMLDivElement>
  contentClassName: string
  tooltipClasses: NonNullable<TooltipProps['classes']>
  tooltipPopperProps: TooltipProps['PopperProps']
  isTooltipOpen: boolean
  onOpen: () => void
  onClose: () => void
}

interface DelayedTooltipController {
  isOpen: boolean
  open: () => void
  close: () => void
}

export const useDelayedTooltip = (isEnabled: boolean): DelayedTooltipController => {
  const closeTimeoutRef = useRef<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current !== null) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const scheduleClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      setIsOpen(false);
      closeTimeoutRef.current = null;
    }, TOOLTIP_CLOSE_DELAY_MS);
  };

  const open = () => {
    if (!isEnabled) {
      return;
    }

    clearCloseTimeout();
    setIsOpen(true);
  };

  const close = () => {
    scheduleClose();
  };

  useEffect(() => () => clearCloseTimeout(), []);

  useEffect(() => {
    if (!isEnabled && isOpen) {
      clearCloseTimeout();
      setIsOpen(false);
    }
  }, [isEnabled, isOpen]);

  return {
    isOpen,
    open,
    close,
  };
};

export const ExpandableTextTooltip: React.FC<ExpandableTextTooltipProps> = ({
  value,
  dataCy,
  contentRef,
  contentClassName,
  tooltipClasses,
  tooltipPopperProps,
  isTooltipOpen,
  onOpen,
  onClose,
}) => (
  <Tooltip
    title={(
      <div
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
      >
        {value}
      </div>
    )}
    arrow
    placement="top"
    classes={tooltipClasses}
    PopperProps={tooltipPopperProps}
    open={isTooltipOpen}
    onOpen={onOpen}
    onClose={onClose}
    disableFocusListener
    disableTouchListener
    interactive
  >
    <div
      ref={contentRef}
      className={contentClassName}
      data-cy={dataCy}
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
    >
      {value}
    </div>
  </Tooltip>
);
